import * as React from "react";
import AnimateHeight from "react-animate-height";
import { Checkbox } from "@utdanningsdirektoratet/checkbox";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { isEmpty } from "@utdanningsdirektoratet/utils/string";
import type { NestedMultiSelectOption } from "./MultiSelect";
import Options from "./Options";
import styles from "./MultiSelect.module.less";

type CheckboxGroupProps<T> = {
  options: NestedMultiSelectOption<T>;
  title: string;
  disabled?: boolean;
  onChange: (valuesToAdd: Array<T>, valuesToRemove: Array<T>) => void;
  values: Array<T>;
  search: string;
  index: number;
  expanded?: boolean;
  focusedRef: React.RefObject<HTMLInputElement> | null;
  focusedIndex: string | null;
  id: string;
  isExpanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
};

const CheckboxGroup = <T,>({
  id,
  isExpanded,
  setExpanded,
  title,
  options,
  search,
  focusedRef,
  focusedIndex,
  index,
  onChange,
  disabled,
  values,
  expanded = false,
}: CheckboxGroupProps<T>): React.ReactElement | null => {
  const _options = React.useMemo(() => (options as NestedMultiSelectOption<T>)?.options ?? [], [options]);
  const _value = React.useMemo(() => (options as NestedMultiSelectOption<Exclude<T, void>>)?.value, [options]);
  const _disabled = React.useMemo(() => (options as NestedMultiSelectOption<T>)?.disabled ?? false, [options]);
  const hasCheckedOptions = React.useMemo(
    () => values.some((v) => _options.map((option) => option.value).indexOf(v) !== -1),
    [_options, values]
  );
  const allOptionsChecked = React.useMemo(
    () => _options.filter((o) => !(o.disabled ?? false)).every((o) => values.map((value) => value).indexOf(o.value) !== -1),
    [_options, values]
  );

  const prevExpanded = React.useRef(isExpanded);

  React.useEffect(() => {
    setExpanded((prevState) => ({
      ...prevState,
      [title]: expanded || hasCheckedOptions || (!!_value && values.indexOf(_value) !== -1),
    }));
  }, [_value, expanded, hasCheckedOptions, setExpanded, title, values]);

  React.useEffect(() => {
    prevExpanded.current = prevExpanded.current || hasCheckedOptions;
  }, [hasCheckedOptions]);

  React.useEffect(() => {
    if (_value && !(values.indexOf(_value) !== -1 || hasCheckedOptions)) {
      if (!expanded) setExpanded((prevState) => ({ ...prevState, [title]: false }));
    }
  }, [hasCheckedOptions, _value, values, expanded, setExpanded, title]);

  if (
    search &&
    title.toLowerCase().indexOf(search.toLowerCase()) === -1 &&
    _options.filter((option) => option.text.toLowerCase().indexOf(search.toLowerCase()) !== -1).length === 0
  )
    return null;

  const optionsClass = cls({
    [styles.multiSelectOptions]: true,
    [styles.multiSelectOptionsInGroup]: true,
  });

  const optionClass = cls({
    [styles.multiSelectGroupheading]: true,
    [styles.multiSelectOption]: true,
    [styles.multiSelectOptionSelected]: focusedIndex === `${index}`,
  });

  return (
    <div role="grop">
      <div className={optionClass} aria-label={title} aria-expanded={isExpanded || !isEmpty(search)} aria-controls={`${id}-group-${index}`}>
        <Checkbox
          label={title}
          role="option"
          id={`${id}-${index}`}
          ref={focusedIndex === `${index}` ? focusedRef : null}
          aria-selected={values.indexOf(_value) !== -1 || hasCheckedOptions}
          disabled={disabled || _disabled}
          checked={values.indexOf(_value) !== -1 || hasCheckedOptions}
          indeterminate={hasCheckedOptions && !allOptionsChecked}
          onChange={(e) => {
            if (!expanded) {
              prevExpanded.current = e.target.checked;
              setExpanded((prevState) => ({
                ...prevState,
                [title]: e.target.checked,
              }));
            }
            onChange(e.target.checked ? [_value] : [], e.target.checked ? [] : [_value, ..._options.map((o) => o.value)]);
          }}
        />
      </div>
      {_options.length !== 0 && (
        <AnimateHeight
          id={`${id}-group-${index}`}
          height={isExpanded || !isEmpty(search) ? "auto" : 0}
          aria-hidden={!isExpanded || isEmpty(search)}
        >
          <div className={optionsClass}>
            <Options
              id={id}
              options={_options}
              values={values}
              focusedRef={focusedRef}
              focusedIndex={focusedIndex}
              disabled={disabled || _disabled}
              onChange={onChange}
              search={search}
              groupTitle={title}
              groupIndex={`${index}`}
              groupValue={_value}
            />
          </div>
        </AnimateHeight>
      )}
    </div>
  );
};

export default CheckboxGroup;
